import { computed, toValue, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import api from '@/app/api/axios.js';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { POLLING_INTERVALS, STALE_TIMES } from '@/app/config/query.js';
import { SiteStatus } from '#modules/sites/models/SiteStatus.js';

export const useSite = () => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();

    const siteId = computed(() => route.params.site);

    const { data, isPending, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['site', siteId],
        queryFn: async () => {
            const id = toValue(siteId);
            const response = await api.get(`/webops/sites/${id}`);
            return response.data;
        },
        retry: (failureCount, error) => {
            // Don't retry on 403/404 errors
            if (error.response && [403, 404].includes(error.response.status)) {
                return false;
            }

            return failureCount < 3;
        },
        staleTime: STALE_TIMES.SITE,
        refetchInterval: (query) => {
            const status = query.state.data?.data?.attributes?.status;

            if (status && status === SiteStatus.Submitted) {
                return false;
            }

            return POLLING_INTERVALS.SITE_DEPLOYING;
        },
        enabled: computed(() => !!siteId.value),
    });

    const environments = computed(() => {
        if (!data.value) {
            return [];
        }

        return data.value.data?.relationships?.environments?.data.map((env) => env.id) ?? [];
    });

    // Handle 403/404 errors outside of queryFn to avoid race conditions
    // Use watch to handle errors reactively without causing navigation loops
    watch(
        [isError, error],
        ([errorState, errorValue]) => {
            if (errorState && errorValue?.response) {
                const status = errorValue.response.status;

                if ([403, 404].includes(status)) {
                    void router.push({ name: 'sites.index' });
                    toast.error(t('sites.not_found'));
                }
            }
        },
        { immediate: true }
    );

    return {
        siteId,
        site: computed(() => data?.value?.data),
        included: computed(() => data?.value?.included || []),
        isPending,
        isLoading,
        isError,
        refetch,
        environments,
    };
};
